import React from "react";
import { Step } from "../../shared/components/StepperForm";
import { FieldType } from "../../shared/interfaces/FieldType";
import { FieldConfig } from "../../shared/components/DynamicForm";
import { useClient } from "../../context/ClientContext";
import { useStepStatus } from "../../shared/hooks/useStepStatus";
import AccordionForm from "../../shared/components/AccordionForm";
import { useNavigate, useParams } from "react-router-dom";
import SharedLayout from "../../layouts/shared-layout/SharedLayout";

interface MyClinicalTrialFormProps {
  trialId?: number;
  defaultValues?: any;
  onSubmit?: (data: { [key: string]: any }) => void;
  onCancel?: () => void;
}

interface FetchOptionsConfig {
  tableName: string;
  endpoint?: string; // default to "/lookup-table/list"
  labelKey?: string; // default to "name"
}

const MyClinicalTrialForm: React.FC<MyClinicalTrialFormProps> = ({
  trialId,
  defaultValues,
  onSubmit,
  onCancel,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [userID, setUserID] = React.useState<number | null>(null);
  React.useEffect(() => {
    const raw = localStorage.getItem("userID");
    const n = raw && raw.trim() !== "" ? Number(raw) : NaN;
    setUserID(Number.isFinite(n) ? n : null);
  }, []);

  // (optional) if your login flow can update userID later in the same tab,
  // emit a custom event after login (window.dispatchEvent(new Event('auth:user')))
  // and listen here to pick it up:
  React.useEffect(() => {
    const handler = () => {
      const raw = localStorage.getItem("userID");
      const n = raw && raw.trim() !== "" ? Number(raw) : NaN;
      setUserID(Number.isFinite(n) ? n : null);
    };
    window.addEventListener("auth:user", handler);
    return () => window.removeEventListener("auth:user", handler);
  }, []);

  React.useEffect(() => {
    if (id && !trialIdState) {
      setTrialIdState(parseInt(id));
    }
  }, [id]);

  const handleCloseViewClinicalTrial = () => {
    navigate("/clinical-trial/list");
  };

  // 1. Initialize client
  const client = useClient("CLINICAL_TRIAL_SERVICE");

  // 2. Manage trialId state
  const [trialIdState, setTrialIdState] = React.useState<number | undefined>(
    trialId
  );

  // 3. Sync trialIdState with trialId if passed (edit mode)
  React.useEffect(() => {
    if (trialId && trialId !== trialIdState) {
      setTrialIdState(trialId);
    }
  }, [trialId, trialIdState]);

  // 4. Step status
  const formStepsStatusEndpoint =
    "/clinical-trial-step/get-by-clinical-trial-id";
  const { stepStatus, loading } = useStepStatus(
    client,
    trialIdState ?? 0,
    formStepsStatusEndpoint
  );

  // 5. Manage loading
  const isLoading = trialIdState ? loading : false;

  // 6. Optionally modify step status if needed
  const modifiedStepStatus = React.useMemo(() => {
    if (stepStatus && stepStatus.current_step?.toLowerCase() === "completed") {
      return { ...stepStatus, current_step: "OpenDataAccess" };
    }
    return stepStatus;
  }, [stepStatus]);

  // 7. Handle final submit event
  const handleFinalSubmit = (data: { [key: string]: any }) => {
    if (onSubmit) {
      onSubmit(data);
    }
  };

  // ────────────────────────────────────────────────
  // Define your steps & fields below
  // ────────────────────────────────────────────────

  /**
   * Utility to fetch paginated select options.
   */
  const handleFetchOptions =
    ({
      tableName,
      endpoint = "/lookup-table/list",
      labelKey = "name",
    }: FetchOptionsConfig) =>
      async (page: number, search: string) => {
        try {
          const payload = {
            page,
            page_size: 10,
            table_name: tableName,
            search,
          };
          const response = await client.post(endpoint, payload);
          const { data, pagination } = response;

          return {
            options: data.map((item: any) => ({
              value: parseInt(item.id, 10),
              label: item[labelKey],
            })),

            totalPages: pagination?.last_page ?? 1,
          };
        } catch (error) {
          console.error(
            `Error fetching ${tableName} options from ${endpoint}:`,
            error
          );
          return {
            options: [],
            totalPages: 0,
          };
        }
      };

  // ────────────────────────────────────────────────
  // Step 1: CreateClinicalTrial
  // ────────────────────────────────────────────────
  const clinicalTrialFields: FieldConfig[] = [
    {
      key: "id",
      label: "ID",
      type: FieldType.INPUT,
      inputType: "number",
      hideField: true,
      size: 12,
    },
    {
      key: "scientific_title",
      label: "Scientific Title",
      type: FieldType.INPUT,
      placeholder: "Enter scientific title",
      validations: { required: true },
      size: 12,
    },
    {
      key: "public_title",
      label: "Public Title",
      type: FieldType.INPUT,
      placeholder: "Enter public title",
      validations: { required: true },
      size: 12,
    },
    {
      key: "scientific_acronym",
      label: "Scientific Acronym",
      type: FieldType.INPUT,
      placeholder: "Enter scientific acronym",
      validations: { required: true },
      size: 6,
    },
    {
      key: "protocol_number",
      label: "Protocol Number",
      type: FieldType.INPUT,
      placeholder: "Protocol Number",
      size: 6,
    },
    {
      key: "protocol_version",
      label: "Protocol Version",
      type: FieldType.INPUT,
      placeholder: "Protocol Version",
      validations: { required: true },
      size: 6,
    },
    {
      key: "registration_number",
      label: "Registration Number",
      type: FieldType.INPUT,
      placeholder: "e.g., NCT123456",
      size: 6,
    },
    {
      key: "registration_status_id",
      label: "Registration Status",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      isMultiple: false,
      fetchOptions: handleFetchOptions({ tableName: "registration_status" }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "registration_status",
        valueKey: "id",
        labelKey: "name",
      },
    },
  ];

  // ────────────────────────────────────────────────
  // Step 2: CreateStudyPurposeDesign
  // ────────────────────────────────────────────────
  const studyPurposeDesignFields: FieldConfig[] = [
    {
      key: "id",
      label: "ID",
      inputType: "number",
      type: FieldType.INPUT,
      hideField: true,
      size: 12,
    },
    {
      key: "purpose_study",
      label: "Purpose Study",
      type: FieldType.INPUT,
      placeholder: "Purpose of the study",
      validations: { required: true },
      size: 12,
    },
    {
      key: "objective_study",
      label: "Objective Study",
      type: FieldType.TEXTAREA,
      placeholder: "Objective of the study",
      validations: { required: true },
      size: 12,
    },
    {
      key: "study_hypothesis",
      label: "Study Hypothesis",
      type: FieldType.TEXTAREA,
      placeholder: "Hypothesis",
      validations: { required: true },
      size: 12,
    },
    {
      key: "type_of_study_id",
      label: "Type of Study",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({ tableName: "type_of_study" }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "type_of_study",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "phase_study_id",
      label: "Phase Study",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({ tableName: "phase_study" }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "phase_study",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "intervention",
      label: "Intervention",
      type: FieldType.INPUT,
      placeholder: "Describe intervention",
      validations: { required: true },
      size: 6,
    },
    {
      key: "randomization_method_name",
      label: "Randomization method name",
      type: FieldType.INPUT,
      placeholder: "Randomization method",
      validations: { required: true },
      size: 6,
    },
    {
      key: "control_group_name",
      label: "Control Group Name",
      type: FieldType.INPUT,
      placeholder: "Control group name",
      size: 6,
    },
    {
      key: "masking_description",
      label: "Masking Description",
      type: FieldType.INPUT,
      placeholder: "Masking details",
      size: 6,
    },
    {
      key: "study_design_control_group_presence",
      label: "Design Control Group Presence?",
      type: FieldType.CHECKBOX,
      placeholder: "",
      size: 6,
    },
    {
      key: "study_design_masking_status",
      label: "Masking Status?",
      type: FieldType.CHECKBOX,
      placeholder: "",
      size: 6,
    },
  ];

  // ────────────────────────────────────────────────
  // Step 3: CreateStudyPopulationEligibility
  // ────────────────────────────────────────────────
  const studyPopulationEligibilityFields: FieldConfig[] = [
    {
      key: "id",
      label: "ID",
      inputType: "number",
      type: FieldType.INPUT,
      hideField: true,
      size: 12,
    },
    {
      key: "health_condition_studied",
      label: "Health Condition Studied",
      type: FieldType.INPUT,
      placeholder: "Health condition studied",
      validations: { required: true },
      size: 12,
    },
    {
      key: "type_of_eligibility_id",
      label: "Type of Eligibility",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({ tableName: "type_of_eligibility" }),
      validations: { required: true },
      size: 12,
      patchFrom: {
        objectKey: "type_of_eligibility",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "eligibility_description",
      label: "Eligibility Description",
      type: FieldType.TEXTAREA,
      placeholder: "Describe eligibility criteria",
      validations: { required: true },
      size: 12,
    },
    {
      key: "target_number_participant",
      label: "Target Number Participant",
      type: FieldType.INPUT,
      inputType: "number",
      validations: { required: true },
      size: 6,
    },
    {
      key: "final_number_participant",
      label: "Final Number Participant",
      type: FieldType.INPUT,
      inputType: "number",
      validations: { required: true },
      size: 6,
    },
    {
      key: "sample_size",
      label: "Sample Size",
      type: FieldType.INPUT,
      inputType: "number",
      validations: { required: true },
      size: 6,
    },
  ];

  // ────────────────────────────────────────────────
  // Step 4: CreateStudyTimelineLocation
  // ────────────────────────────────────────────────
  const studyTimelineLocationFields: FieldConfig[] = [
    {
      key: "id",
      label: "ID",
      inputType: "number",
      type: FieldType.INPUT,
      hideField: true,
      size: 12,
    },
    {
      key: "study_duration",
      label: "Study Duration(years)",
      type: FieldType.INPUT,
      inputType: "number",
      validations: { required: true },
      size: 6,
    },
    {
      key: "country_recruitment_id",
      label: "Country Recruitment",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({
        tableName: "country_recruitment",
        endpoint: "/country/list",
      }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "country",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "location_study_site",
      label: "Study Site Location",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "study_participating_center_physical_address",
      label: "Center Physical Address",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "study_participating_center_postal_address",
      label: "Center Postal Address",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "study_participating_center_region_id",
      label: "Center Region",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({
        tableName: "region",
        endpoint: "/region/list",
      }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "region",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "anticipated_start_date",
      label: "Anticipated Start Date",
      type: FieldType.INPUT,
      inputType: "date",
      validations: { required: true },
      size: 6,
    },
    {
      key: "anticipated_end_date",
      label: "Anticipated End Date",
      type: FieldType.INPUT,
      inputType: "date",
      validations: { required: true },
      size: 6,
    },
    {
      key: "recruitment_status_id",
      label: "Recruitment Status",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({ tableName: "recruitment_status" }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "recruitment_status",
        valueKey: "id",
        labelKey: "name",
      },
    },
  ];

  // ────────────────────────────────────────────────
  // Step 5: CreateContactInvestigator
  // ────────────────────────────────────────────────
  const contactInvestigatorFields: FieldConfig[] = [
    {
      key: "id",
      label: "ID",
      inputType: "number",
      type: FieldType.INPUT,
      hideField: true,
      size: 12,
    },
    {
      key: "investigator_role_id",
      label: "Investigator Role",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({ tableName: "investigator_role" }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "investigator_role",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "institution_id",
      label: "Institution",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({
        tableName: "institution",
        endpoint: "/institution/list",
      }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "institution",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "country_id",
      label: "Country",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({
        tableName: "country",
        endpoint: "/country/list",
      }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "country",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "name",
      label: "Name",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "mobile_number",
      label: "Mobile Number",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "email_address",
      label: "Email Address",
      type: FieldType.INPUT,
      inputType: "email",
      validations: { required: true },
      size: 6,
    },
    {
      key: "postal_address",
      label: "Postal Address",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "city",
      label: "City",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
  ];

  // ────────────────────────────────────────────────
  // Step 6: CreateEthicalRegulatoryApproval
  // ────────────────────────────────────────────────
  const ethicalRegulatoryApprovalFields: FieldConfig[] = [
    {
      key: "id",
      label: "ID",
      inputType: "number",
      type: FieldType.INPUT,
      hideField: true,
      size: 12,
    },
    {
      key: "ethical_regulatory_id",
      label: "Ethical Regulatory",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({
        tableName: "ethical_regulatory",
        endpoint: "/ethical-regulatory/list",
        labelKey: "name_ethical_authority",
      }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "ethical_regulatory",
        valueKey: "id",
        labelKey: "name_ethical_authority",
      },
    },
    {
      key: "is_approved_ethic_committee",
      label: "Approved by Ethics Committee?",
      type: FieldType.CHECKBOX,
      validations: { required: true },
      size: 6,
    },
    {
      key: "document_number",
      label: "Document Number",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "document_path",
      label: "Document Path",
      type: FieldType.FILE,
      validations: { required: true },
      size: 6,
    },
  ];

  // ────────────────────────────────────────────────
  // Step 7: CreateFundingSponsorship
  // ────────────────────────────────────────────────
  const fundingSponsorshipFields: FieldConfig[] = [
    {
      key: "id",
      label: "ID",
      inputType: "number",
      type: FieldType.INPUT,
      hideField: true,
      size: 12,
    },
    {
      key: "name",
      label: "Funder/Sponsor Name",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "amount",
      label: "Amount",
      type: FieldType.INPUT,
      inputType: "number",
      validations: { required: true },
      size: 6,
    },
    {
      key: "country_id",
      label: "Country",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({
        tableName: "country",
        endpoint: "/country/list",
      }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "country",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "funding_sector_id",
      label: "Funding Sector",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({ tableName: "funding_sector" }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "funding_sector",
        valueKey: "id",
        labelKey: "name",
      },
    },
  ];

  // ────────────────────────────────────────────────
  // Step 8: CreateStudyDescriptionScientificPublic
  // ────────────────────────────────────────────────
  const studyDescriptionScientificPublicFields: FieldConfig[] = [
    {
      key: "id",
      label: "ID",
      inputType: "number",
      type: FieldType.INPUT,
      hideField: true,
      size: 12,
    },
    {
      key: "study_website",
      label: "Study Website",
      type: FieldType.INPUT,
      placeholder: "https://example.com",
      validations: { required: true },
      size: 12,
    },
    {
      key: "lay_summary",
      label: "Lay Summary",
      type: FieldType.TEXTAREA,
      validations: { required: true },
      size: 6,
    },
    {
      key: "scientific_summary",
      label: "Scientific Summary",
      type: FieldType.TEXTAREA,
      validations: { required: true },
      size: 6,
    },
  ];

  // ────────────────────────────────────────────────
  // Step 9: CreateStudyInterventionOutcome
  // ────────────────────────────────────────────────
  const studyInterventionOutcomeFields: FieldConfig[] = [
    {
      key: "id",
      label: "ID",
      inputType: "number",
      type: FieldType.INPUT,
      hideField: true,
      size: 12,
    },
    {
      key: "intervention_name",
      label: "Intervention Name",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "intervention_description",
      label: "Intervention Description",
      type: FieldType.TEXTAREA,
      validations: { required: true },
      size: 6,
    },
    {
      key: "control_comparator",
      label: "Control Comparator",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "type_of_outcome_id",
      label: "Type of Outcome",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({ tableName: "type_of_outcome" }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "type_of_outcome",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "outcome_description",
      label: "Outcome Description",
      type: FieldType.TEXTAREA,
      validations: { required: true },
      size: 12,
    },
  ];

  // ────────────────────────────────────────────────
  // Step 10: CreateStudyResultPublication
  // ────────────────────────────────────────────────
  const studyResultPublicationFields: FieldConfig[] = [
    {
      key: "id",
      label: "ID",
      inputType: "number",
      type: FieldType.INPUT,
      hideField: true,
      size: 12,
    },
    {
      key: "permission_to_publish",
      label: "Permission to Publish?",
      type: FieldType.CHECKBOX,
      size: 12,
    },
    {
      key: "summary_results",
      label: "Summary Results",
      type: FieldType.TEXTAREA,
      validations: { required: true },
      size: 12,
    },
    {
      key: "authority_committee_name",
      label: "Authority Committee Name",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "publication_type_id",
      label: "Publication Type",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({ tableName: "publication_type" }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "publication_type",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "publisher_id",
      label: "Publisher",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({ tableName: "publisher" }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "publisher",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "publication_title",
      label: "Publication Title",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "url_doi",
      label: "URL / DOI",
      type: FieldType.INPUT,
      placeholder: "https://doi.org/...",
      validations: { required: true },
      size: 6,
    },
  ];

  // ────────────────────────────────────────────────
  // Step 11: CreateOpenDataAccess
  // ────────────────────────────────────────────────
  const openDataAccessFields: FieldConfig[] = [
    {
      key: "id",
      label: "ID",
      inputType: "number",
      type: FieldType.INPUT,
      hideField: true,
      size: 12,
    },
    {
      key: "allow_publish",
      label: "Allow Publish",
      type: FieldType.CHECKBOX,
      size: 12,
    },
    {
      key: "repository_name",
      label: "Repository Name",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "statistical_method_used",
      label: "Statistical Method Used",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "study_identification_variable_id",
      label: "Study Identification Variable",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({
        tableName: "study_identification_variable",
      }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "study_identification_variable",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "statistically_significant_pvalue_id",
      label: "Significant P-Value",
      type: FieldType.PAGINATED_SELECT,
      inputType: "number",
      fetchOptions: handleFetchOptions({
        tableName: "statistically_significant_pvalue",
      }),
      validations: { required: true },
      size: 6,
      patchFrom: {
        objectKey: "statistically_significant_p_value",
        valueKey: "id",
        labelKey: "name",
      },
    },
    {
      key: "sensitivity_analysis_result",
      label: "Sensitivity Analysis Result",
      type: FieldType.TEXTAREA,
      validations: { required: true },
      size: 6,
    },
    {
      key: "effect_size_metric",
      label: "Effect Size Metric",
      type: FieldType.INPUT,
      inputType: "number",
      validations: { required: true },
      size: 6,
    },
    {
      key: "effect_size_value",
      label: "Effect Size Value",
      type: FieldType.INPUT,
      inputType: "number",
      validations: { required: true },
      size: 6,
    },
    {
      key: "confidence_interval",
      label: "Confidence Interval",
      type: FieldType.INPUT,
      inputType: "number",
      placeholder: "e.g. 95%",
      validations: { required: true },
      size: 6,
    },
    {
      key: "adjustment_multiple_comparison",
      label: "Adjustment Multiple Comparison",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 12,
    },
    {
      key: "handling_missing_data",
      label: "Handling Missing Data",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 12,
    },
    {
      key: "data_accessibility_variables_document_path",
      label: "Doc Path for Accessibility Variables",
      type: FieldType.FILE,
      validations: { required: true },
      size: 12,
    },
    {
      key: "quality_assessment_variable",
      label: "Quality Assessment Variable",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "risk_of_bias_assessment",
      label: "Risk of Bias Assessment",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "study_limitation",
      label: "Study Limitation",
      type: FieldType.TEXTAREA,
      validations: { required: true },
      size: 6,
    },
    {
      key: "funding_source",
      label: "Funding Source",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "potential_conflict_interest",
      label: "Potential Conflict of Interest",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "additional_variable",
      label: "Additional Variable",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "publication_bias_indicator",
      label: "Publication Bias Indicator",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
    {
      key: "heterogeneity_measure",
      label: "Heterogeneity Measure",
      type: FieldType.INPUT,
      validations: { required: true },
      size: 6,
    },
  ];

  /**
   * 1) The fields,
   * 2) The endpoint to call for creation,
   * 3) The endpoint to fetch existing data,
   * 4) Additional values to pass in each request
   */
  const createSteps = (trialId?: number, userID?: number | null): Step[] => [
    {
      title: "Clinical Trial",
      icon: "science",
      fields: clinicalTrialFields,
      endpoint_create: "/clinical-trial/create",
      endpoint_get: "/clinical-trial/get-by-id",
      additionalValues: {
        clinical_trial_id: trialId,
        researcher_id: userID,
        created_by: userID,
        action: "submit",
      },
      statusKey: "ClinicalTrial",
    },
    {
      title: "Study Purpose and Design",
      icon: "description",
      fields: studyPurposeDesignFields,
      endpoint_create: "/study-purpose-design/create",
      endpoint_get: "/study-purpose-design/get-by-clinical-trial-id",
      additionalValues: {
        clinical_trial_id: trialId,
        created_by: userID,
        action: "submit",
      },
      statusKey: "StudyPurposeDesign",
    },
    {
      title: "Study Population Eligibility",
      icon: "group",
      fields: studyPopulationEligibilityFields,
      endpoint_create: "/study-population-eligibility/create",
      endpoint_get: "/study-population-eligibility/get-by-clinical-trial-id",
      additionalValues: {
        clinical_trial_id: trialId,
        created_by: userID,
        action: "submit",
      },
      statusKey: "StudyPopulationEligibility",
    },
    {
      title: "Study Timeline and Location",
      icon: "calendar_today",
      fields: studyTimelineLocationFields,
      endpoint_create: "/study-timeline-location/create",
      endpoint_get: "/study-timeline-location/get-by-clinical-trial-id",
      additionalValues: {
        clinical_trial_id: trialId,
        created_by: userID,
        action: "submit",
      },
      statusKey: "StudyTimelineLocation",
    },
    {
      title: "Contact Investigator",
      sendAsArray: true,
      icon: "contact_phone",
      fields: contactInvestigatorFields,
      endpoint_create: "/contact-investigator/create",
      endpoint_get: "/contact-investigator/list-by-clinical-trial-id",
      additionalValues: {
        clinical_trial_id: trialId,
        created_by: userID,
        action: "submit",
      },
      statusKey: "ContactInvestigator",
    },
    {
      title: "Ethical Regulatory Approval",
      icon: "gavel",
      fields: ethicalRegulatoryApprovalFields,
      endpoint_create: "/ethical-regulatory-approval/create",
      endpoint_get: "/ethical-regulatory-approval/get-by-clinical-trial-id",
      additionalValues: {
        clinical_trial_id: trialId,
        created_by: userID,
        action: "submit",
      },
      statusKey: "EthicalRegulatoryApproval",
    },
    {
      title: "Funding Sponsorship",
      icon: "attach_money",
      fields: fundingSponsorshipFields,
      endpoint_create: "/funding-sponsorship/create",
      endpoint_get: "/funding-sponsorship/get-by-clinical-trial-id",
      additionalValues: {
        clinical_trial_id: trialId,
        created_by: userID,
        action: "submit",
      },
      statusKey: "FundingSponsorship",
    },
    {
      title: "Study Description (Lay and Scientific)",
      icon: "notes",
      fields: studyDescriptionScientificPublicFields,
      endpoint_create: "/study-description-scientific-public/create",
      endpoint_get:
        "/study-description-scientific-public/get-by-clinical-trial-id",
      additionalValues: {
        clinical_trial_id: trialId,
        created_by: userID,
        action: "submit",
      },
      statusKey: "StudyDescriptionScientificPublic",
    },
    {
      title: "Study Intervention and Outcome",
      icon: "build_circle",
      fields: studyInterventionOutcomeFields,
      endpoint_create: "/study-intervention-outcome/create",
      endpoint_get: "/study-intervention-outcome/get-by-clinical-trial-id",
      additionalValues: {
        clinical_trial_id: trialId,
        created_by: userID,
        action: "submit",
      },
      statusKey: "StudyInterventionOutcome",
    },
    {
      title: "Study Result Publication",
      sendAsArray: true,
      icon: "publish",
      fields: studyResultPublicationFields,
      endpoint_create: "/study-result-publication/create",
      endpoint_get: "/study-result-publication/list-by-clinical-trial-id",
      additionalValues: {
        clinical_trial_id: trialId,
        created_by: userID,
        action: "submit",
      },
      statusKey: "StudyResultPublication",
    },
    {
      title: "Open Data Access",
      icon: "cloud_upload",
      fields: openDataAccessFields,
      endpoint_create: "/open-data-access/create",
      endpoint_get: "/open-data-access/get-by-clinical-trial-id",
      additionalValues: {
        clinical_trial_id: trialId,
        created_by: userID,
        action: "submit",
      },
      statusKey: "OpenDataAccess",
    },
  ];

  // 8 Generate steps with the current trialIdState and userID
  const steps = React.useMemo(
    () => createSteps(trialIdState, userID),
    [trialIdState, userID]
  );

  return (
    <SharedLayout
      formTitle="Create Clinical Trial"
      onCloseDetails={handleCloseViewClinicalTrial}
      animationSize="eighty"
      hideAdd={true}
      addPermission={["/auth/login"]}
    >
      {isLoading || userID === null ? (
        <div>Loading...</div>
      ) : (
        <AccordionForm
          steps={steps}
          onSubmit={handleFinalSubmit}
          client={client}
          stepStatus={modifiedStepStatus || undefined}
          onCancel={onCancel}
          filterID={trialIdState}
          output={(response, step) => {
            if (step.title === "Clinical Trial") {
              const newId =
                response?.data?.id ??
                response?.id ??
                response?.data?.clinical_trial_id ??
                response?.clinical_trial_id ??
                null;
              if (newId) setTrialIdState(Number(newId));
            }
          }}
        />
      )}
    </SharedLayout>
  );
};

export default MyClinicalTrialForm;
